import React, { useState } from "react";
import { toast } from "react-toastify";
import { buildTopicPayload } from "../../payloads/buildTopicPayload";
import { webinarService } from "../../services/webinarService";
import { WebinarServiceError } from "../../types/dto";
import { WebinarTopic } from "../../types/models";
import "./TopicPicker.scss";

type Props = {
  topics: WebinarTopic[];
  value: string;
  onChange: (topicId: string) => void;
  onTopicsChange: (topics: WebinarTopic[]) => void;
};

const TopicPicker: React.FC<Props> = ({
  topics,
  value,
  onChange,
  onTopicsChange,
}) => {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const canAddTopic = draft.trim().length >= 2;

  const saveTopic = async () => {
    if (!canAddTopic || saving) return;
    setSaving(true);
    try {
      const payload = buildTopicPayload(draft, {
        usedColors: topics.map((t) => t.fg),
      });
      const created = await webinarService.addTopic(payload);
      onTopicsChange([...topics, created]);
      onChange(created.id);
      setDraft("");
      setAdding(false);
      toast.success(`Topic “${created.name}” added and selected`);
    } catch (e) {
      toast.error(
        e instanceof WebinarServiceError
          ? e.message
          : e instanceof Error
          ? e.message
          : "Could not add topic"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wf-topic-picker">
      {topics.length === 0 && !adding ? (
        <div className="wf-topic-picker__empty">
          No topics yet. Add one to tag this session.
        </div>
      ) : null}
      <div className="wf-topic-picker__chips">
        {topics.map((t) => {
          const active = t.id === value;
          return (
            <button
              key={t.id}
              type="button"
              className={`wf-topic-chip${active ? " is-active" : ""}`}
              style={{
                background: active ? t.bg : "#FFFFFF",
                color: t.fg,
                borderColor: active ? t.fg : "#ECE7E1",
              }}
              onClick={() => onChange(t.id)}
            >
              {t.name}
            </button>
          );
        })}

        {adding ? (
          <span className="wf-topic-picker__inline-add">
            <input
              type="text"
              value={draft}
              maxLength={24}
              autoFocus
              placeholder="New topic name"
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canAddTopic) saveTopic();
                if (e.key === "Escape") {
                  setAdding(false);
                  setDraft("");
                }
              }}
            />
            <button
              type="button"
              className="ok"
              title="Add topic"
              disabled={saving || !canAddTopic}
              onClick={saveTopic}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </button>
            <button
              type="button"
              className="cancel"
              title="Cancel"
              onClick={() => {
                setAdding(false);
                setDraft("");
              }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </span>
        ) : (
          <button
            type="button"
            className="wf-topic-picker__start"
            onClick={() => setAdding(true)}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
            {topics.length === 0 ? "Add first topic" : "Add topic"}
          </button>
        )}
      </div>
    </div>
  );
};

export default TopicPicker;
